import { lazy, Suspense, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import PageHeader from '@/components/settings/PageHeader'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import platformSubscriptionsApi from '@wtf/shared/api/platformSubscriptions'
import { usePricing } from '@/hooks/api/usePricing'
import { captureException } from '@/config/sentry'
import { isIOSApp } from '@/utils/platformDetection'

const CancelSubscriptionBottomSheet = lazy(
    () =>
        import(
            '@/components/subscriptions/bottom-sheets/CancelSubscriptionBottomSheet'
        )
)

const DowngradeSubscriptionBottomSheet = lazy(
    () =>
        import(
            '@/components/subscriptions/bottom-sheets/DowngradeSubscriptionBottomSheet'
        )
)

export const Route = createFileRoute('/settings/subscriptions/manage')({
    component: RouteComponent,
})

function RouteComponent() {
    return <ManageSubscriptionPage />
}

const ManageSubscriptionPage = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const [targetDowngradePlan, setTargetDowngradePlan] = useState<
        'FREE' | 'PRO' | 'MAX'
    >('FREE')

    const { data: platformSubscription, isLoading: isLoadingSubscription } =
        useQuery({
            queryKey: ['platform-subscription'],
            queryFn: () => platformSubscriptionsApi.getCurrentSubscription(),
            staleTime: 0, // Always fetch fresh data
            refetchOnMount: 'always', // Force refetch when component mounts
        })

    // Fetch billing history
    const { data: billingHistory, isLoading: isLoadingBillingHistory } =
        useQuery({
            queryKey: ['billing-history'],
            queryFn: () => platformSubscriptionsApi.getBillingHistory(),
            staleTime: 5 * 60 * 1000, // 5 minutes
            enabled:
                !!platformSubscription && platformSubscription.plan !== 'FREE',
        })

    // Fetch pricing from Stripe
    const { data: pricing } = usePricing()

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A'
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
    }

    const getStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            { bg: string; text: string; label: string }
        > = {
            ACTIVE: {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: 'Active',
            },
            TRIALING: {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: 'Trialing',
            },
            PAST_DUE: {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                label: 'Past Due',
            },
            CANCELED: {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Canceled',
            },
            INCOMPLETE: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Incomplete',
            },
        }
        const badge = statusMap[status] || statusMap.ACTIVE
        return (
            <span
                className={`px-3 py-1 ${badge.bg} ${badge.text} rounded-full text-sm font-medium`}
            >
                {badge.label}
            </span>
        )
    }

    const getInvoiceStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            { bg: string; text: string; label: string }
        > = {
            paid: {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: 'Paid',
            },
            open: {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: 'Open',
            },
            draft: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Draft',
            },
            uncollectible: {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Failed',
            },
            void: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Void',
            },
        }
        const badge = statusMap[status] || statusMap.draft
        return (
            <span
                className={`px-2 py-1 ${badge.bg} ${badge.text} rounded-full text-xs font-medium`}
            >
                {badge.label}
            </span>
        )
    }

    const handleUpgrade = () => {
        const currentPlan = platformSubscription?.plan || 'FREE'
        const nextPlan = currentPlan === 'FREE' ? 'pro' : 'max'
        navigate({
            to: '/pricing',
            search: {
                preselect: nextPlan,
                returnToPath: undefined,
                reason: undefined,
            },
        })
    }

    const handleDowngrade = () => {
        const currentPlan = platformSubscription?.plan || 'FREE'
        if (currentPlan === 'MAX') {
            setTargetDowngradePlan('PRO')
        } else if (currentPlan === 'PRO') {
            setTargetDowngradePlan('FREE')
        }
        setShowDowngradeModal(true)
    }

    const handleCancel = () => {
        setShowCancelModal(true)
    }

    const handleReactivate = async () => {
        try {
            await platformSubscriptionsApi.reactivateSubscription()
            // Refetch subscription to show updated state
            queryClient.invalidateQueries({
                queryKey: ['platform-subscription'],
            })
        } catch (error) {
            console.error('Failed to reactivate subscription:', error)
            captureException(error, { action: 'reactivate_subscription' })
        }
    }

    if (isLoadingSubscription) {
        return (
            <div className="h-[100dvh] overflow-y-auto bg-white px-2">
                <PageHeader title="Manage subscriptions" />
                <div className="flex items-center justify-center py-20">
                    <div className="text-gray-600">Loading subscription...</div>
                </div>
            </div>
        )
    }

    const percentage = platformSubscription
        ? (platformSubscription.tryOnsUsed / platformSubscription.tryOnsLimit) *
          100
        : 0

    const currentPlan = platformSubscription?.plan || 'FREE'
    const canUpgrade = currentPlan !== 'MAX'
    const canDowngrade = currentPlan !== 'FREE'

    return (
        <>
            <div className="h-[100dvh] overflow-y-auto bg-white px-2 pb-20">
                <PageHeader title="Manage subscriptions" />

                <div className="flex flex-col gap-4 p-2">
                    {/* Current Plan Card */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold">
                                Current Plan
                            </h3>
                            {platformSubscription &&
                                getStatusBadge(platformSubscription.status)}
                        </div>
                        <p className="mb-4 text-2xl font-bold">
                            {platformSubscription?.plan || 'FREE'} Plan
                        </p>

                        {/* Upgrade Button Only */}
                        {/* TEMPORARY: Phase I iOS restriction - Remove in Phase II
                            Hide upgrade button on iOS to comply with App Store guidelines
                            See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md */}
                        {canUpgrade && !isIOSApp() && (
                            <button
                                onClick={handleUpgrade}
                                className="w-full rounded-xl bg-black px-4 py-3 text-white transition hover:bg-gray-800"
                            >
                                Upgrade Plan
                            </button>
                        )}
                    </div>

                    {/* Pending Downgrade Warning */}
                    {platformSubscription?.scheduledPlan && (
                        <div className="rounded-2xl border-2 border-yellow-200 bg-yellow-50 p-6 shadow-sm">
                            <div className="flex items-start gap-3">
                                <svg
                                    className="mt-0.5 h-6 w-6 flex-shrink-0 text-yellow-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <div className="flex-1">
                                    <h4 className="mb-1 text-sm font-semibold text-yellow-800">
                                        Subscription Change Scheduled
                                    </h4>
                                    <p className="mb-3 text-sm text-yellow-800">
                                        Your subscription will be downgraded to{' '}
                                        <span className="font-medium">
                                            {platformSubscription.scheduledPlan}
                                        </span>{' '}
                                        on{' '}
                                        <span className="font-medium">
                                            {formatDate(
                                                platformSubscription.currentPeriodEnd
                                            )}
                                        </span>
                                        . You'll keep your current plan benefits
                                        until then.
                                    </p>
                                    <button
                                        onClick={handleReactivate}
                                        className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-yellow-700"
                                    >
                                        Keep Current Subscription
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Try-On Credits */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h3 className="mb-2 text-lg font-semibold">
                            Try-On Credits
                        </h3>
                        <p className="mb-1 text-3xl font-bold">
                            {platformSubscription?.tryOnsRemaining || 0}
                        </p>
                        <p className="mb-4 text-sm text-gray-600">
                            credits remaining out of{' '}
                            {platformSubscription?.tryOnsLimit || 0} monthly
                            credits
                        </p>
                        {/* Progress bar */}
                        <div className="h-2 w-full rounded-full bg-gray-200">
                            <div
                                className="h-2 rounded-full bg-blue-500 transition-all"
                                style={{
                                    width: `${Math.min(percentage, 100)}%`,
                                }}
                            />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            {platformSubscription?.tryOnsUsed || 0} used this
                            billing period
                        </p>
                    </div>

                    {/* Billing Information */}
                    {platformSubscription && currentPlan !== 'FREE' && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                            <h3 className="mb-4 text-lg font-semibold">
                                Billing Information
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">
                                        {platformSubscription.scheduledPlan
                                            ? 'Downgrade effective date'
                                            : 'Next billing date'}
                                    </span>
                                    <span className="font-medium">
                                        {formatDate(
                                            platformSubscription.currentPeriodEnd
                                        )}
                                    </span>
                                </div>
                                {platformSubscription.scheduledPlan ? (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">
                                                Current amount
                                            </span>
                                            <span className="font-medium">
                                                {pricing
                                                    ? `${pricing[currentPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                    : 'Loading...'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">
                                                After downgrade
                                            </span>
                                            <span className="font-medium text-yellow-700">
                                                {platformSubscription.scheduledPlan ===
                                                'FREE'
                                                    ? 'Free'
                                                    : pricing
                                                      ? `${pricing[platformSubscription.scheduledPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                      : 'Loading...'}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600">
                                            Amount
                                        </span>
                                        <span className="font-medium">
                                            {pricing
                                                ? `${pricing[currentPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                : 'Loading...'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Billing History */}
                    {platformSubscription && currentPlan !== 'FREE' && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                            <h3 className="mb-4 text-lg font-semibold">
                                Billing History
                            </h3>
                            {isLoadingBillingHistory ? (
                                <div className="py-8 text-center text-gray-600">
                                    Loading invoices...
                                </div>
                            ) : billingHistory?.invoices &&
                              billingHistory.invoices.length > 0 ? (
                                <div className="space-y-3">
                                    {billingHistory.invoices.map((invoice) => (
                                        <div
                                            key={invoice.id}
                                            className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                                        >
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">
                                                        {invoice.number ||
                                                            'Invoice'}
                                                    </span>
                                                    {getInvoiceStatusBadge(
                                                        invoice.status
                                                    )}
                                                </div>
                                                <span className="text-sm text-gray-600">
                                                    {formatDate(invoice.date)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold">
                                                    {invoice.currency}{' '}
                                                    {invoice.amount.toFixed(2)}
                                                </span>
                                                {invoice.hostedInvoiceUrl && (
                                                    <a
                                                        href={
                                                            invoice.hostedInvoiceUrl
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="rounded-lg border border-gray-300 px-3 py-1 text-sm transition hover:bg-gray-50"
                                                    >
                                                        View
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="rounded-lg bg-gray-50 py-8 text-center">
                                    <p className="text-gray-600">
                                        No invoices yet
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Your billing history will appear here
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Billing Period Info */}
                    {platformSubscription && (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <p className="text-xs text-gray-600">
                                Current period:{' '}
                                {formatDate(
                                    platformSubscription.currentPeriodStart ||
                                        platformSubscription.billingPeriodStart
                                )}{' '}
                                -{' '}
                                {formatDate(
                                    platformSubscription.currentPeriodEnd ||
                                        platformSubscription.billingPeriodEnd
                                )}
                            </p>
                        </div>
                    )}

                    {/* Downgrade and Cancel Actions */}
                    {currentPlan !== 'FREE' && (
                        <div className="flex flex-col gap-2 pt-4">
                            {/* TEMPORARY: Phase I iOS restriction - Remove in Phase II
                                Hide downgrade button on iOS to comply with App Store guidelines
                                See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md */}
                            {canDowngrade && !isIOSApp() && (
                                <button
                                    onClick={handleDowngrade}
                                    className="w-full rounded-xl border border-gray-300 px-4 py-3 transition hover:bg-gray-50"
                                >
                                    Downgrade Plan
                                </button>
                            )}
                            <button
                                onClick={handleCancel}
                                className="w-full rounded-xl border border-red-500 px-4 py-3 text-red-500 transition hover:bg-red-50"
                            >
                                Cancel Subscription
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Cancel Subscription Bottom Sheet */}
            {showCancelModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <CancelSubscriptionBottomSheet
                        open={showCancelModal}
                        onClose={() => setShowCancelModal(false)}
                    />
                </Suspense>
            )}

            {/* Downgrade Subscription Bottom Sheet */}
            {showDowngradeModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <DowngradeSubscriptionBottomSheet
                        open={showDowngradeModal}
                        onClose={() => setShowDowngradeModal(false)}
                        currentPlan={platformSubscription?.plan || 'FREE'}
                        targetPlan={targetDowngradePlan}
                    />
                </Suspense>
            )}
        </>
    )
}
